/**
 * CAM-S4 / FE-A4 — tenant camera fleet hub (#/account/cameras).
 * Catalog CRUD + per-device bind inspector. Discovery stays on #/devices/:id.
 */

import {
  CAMERAS_CONSOLE_TIMEOUT_MS,
  cameraApiPathWithOwner,
  buildGetAdminCamerasPath,
  buildPostAdminCamerasPath,
  buildDeleteAdminCamerasPath,
  buildPostAdminCamerasProbeTestPath,
  buildGetAdminDevicesCameraBindingsPath,
  buildPostAdminDevicesCameraBindingsPath,
  buildDeleteAdminDevicesCameraBindingsPath,
} from "./cameras-console-api.js";
import { buildGetDevicesPath, DEVICES_CONSOLE_TIMEOUT_MS } from "./devices-console-api.js";
import { softApiError, softOperatorNote } from "./format.js";

/**
 * @param {{ escapeHtml?: (s: unknown) => string }} [_deps]
 * @returns {string}
 */
export function camerasFleetHubShellHtml(_deps) {
  return `
<section class="card cam-fleet" aria-label="Cameras">
  <div class="row" style="justify-content:space-between;align-items:flex-start;gap:12px;flex-wrap:wrap;margin-bottom:8px">
    <div>
      <h2 style="margin:0">Cameras</h2>
      <p class="muted" style="margin:6px 0 0;max-width:42rem;line-height:1.45">
        Account cameras for alarm snapshots. Bind a camera to a board here, or open a board drawer for network camera search.
        Credentials are never shown after save.
      </p>
    </div>
    <div class="row" style="gap:8px;flex-wrap:wrap">
      <button type="button" class="btn secondary btn-tap" data-cc-go="snapshots">Evidence</button>
      <a class="btn secondary btn-tap" href="#/group/_all">All boards</a>
    </div>
  </div>
  <p id="camFleetStatus" class="muted" style="min-height:1.2em;margin:0 0 12px" role="status"></p>
  <div id="camFleetCatalog" class="muted">Loading cameras…</div>
</section>

<section class="card cam-fleet" style="margin-top:14px">
  <h3 style="margin:0 0 8px">Add camera</h3>
  <p class="muted" style="margin:0 0 10px;font-size:13px">Probe checks reachability from the console host. Prefer board Test capture for the edge path.</p>
  <form id="camFleetAddForm" class="inline-form">
    <label class="field"><span>Label</span><input id="camFleetLabel" placeholder="Gate cam" maxlength="120" /></label>
    <label class="field"><span>Host</span><input id="camFleetHost" placeholder="192.168.1.64" required maxlength="255" /></label>
    <label class="field"><span>Protocol</span>
      <select id="camFleetProto">
        <option value="onvif">Network camera</option>
        <option value="http_snapshot">HTTP snapshot</option>
        <option value="hikvision">Hikvision ISAPI</option>
        <option value="dahua">Dahua</option>
      </select>
    </label>
    <label class="field"><span>Username</span><input id="camFleetUser" autocomplete="off" maxlength="64" /></label>
    <label class="field"><span>Password</span><input id="camFleetPass" type="password" autocomplete="new-password" maxlength="128" /></label>
    <div class="row wide" style="justify-content:flex-end;gap:8px">
      <button class="btn secondary" type="button" id="camFleetProbe">Probe</button>
      <button class="btn" type="submit">Save camera</button>
    </div>
    <p id="camFleetProbeMsg" class="muted" style="margin-top:8px;font-size:12px"></p>
  </form>
</section>

<section class="card cam-fleet" style="margin-top:14px">
  <h3 style="margin:0 0 8px">Board bindings</h3>
  <p class="muted" style="margin:0 0 10px;font-size:13px">
    Bindings are per board (alarm → snapshot). Pick a board to list or change binds.
    Network camera discovery lives on the board Cameras panel.
  </p>
  <div class="row" style="gap:8px;flex-wrap:wrap;align-items:flex-end;margin-bottom:12px">
    <label class="field" style="min-width:220px;flex:1"><span>Board</span>
      <select id="camFleetDevice"></select>
    </label>
    <label class="field" style="min-width:220px;flex:1"><span>Camera</span>
      <select id="camFleetBindCam"></select>
    </label>
    <label class="field" style="width:100px"><span>Cooldown</span>
      <input id="camFleetCooldown" type="number" min="5" max="600" value="20" />
    </label>
    <button type="button" class="btn" id="camFleetBindBtn">Bind</button>
  </div>
  <div id="camFleetBindings" class="muted">Select a board to load bindings.</div>
</section>`;
}

/**
 * @param {HTMLElement} view
 * @param {{
 *   ownerAdmin?: string,
 *   routeSeq: number,
 *   isRouteCurrent: (n: number) => boolean,
 *   api: Function,
 *   toast: Function,
 *   escapeHtml: (s: unknown) => string,
 *   timeoutMs?: number,
 * }} ctx
 */
export function wireCamerasFleetHub(view, ctx) {
  const {
    ownerAdmin = "",
    routeSeq,
    isRouteCurrent,
    api,
    toast,
    escapeHtml,
    timeoutMs: timeoutMsIn,
  } = ctx;
  const camPath = (p) => cameraApiPathWithOwner(p, ownerAdmin);
  const timeoutMs = timeoutMsIn || CAMERAS_CONSOLE_TIMEOUT_MS;
  const esc = typeof escapeHtml === "function" ? escapeHtml : (s) => String(s ?? "");

  const statusEl = view.querySelector("#camFleetStatus");
  const catalogEl = view.querySelector("#camFleetCatalog");
  const deviceSel = view.querySelector("#camFleetDevice");
  const camSel = view.querySelector("#camFleetBindCam");
  const bindingsEl = view.querySelector("#camFleetBindings");
  const probeMsg = view.querySelector("#camFleetProbeMsg");

  /** @type {Array<Record<string, unknown>>} */
  let cameras = [];
  /** @type {Array<{ id: string, label: string }>} */
  let devices = [];

  const setStatus = (txt, kind) => {
    if (!statusEl) return;
    statusEl.textContent = String(txt || "");
    statusEl.className = kind === "err" ? "badge offline" : kind === "ok" ? "badge online" : "muted";
  };

  const fillCameraSelect = () => {
    if (!camSel) return;
    if (!cameras.length) {
      camSel.innerHTML = `<option value="">No cameras</option>`;
      return;
    }
    camSel.innerHTML = cameras
      .map((c) => {
        const id = String(c.camera_id || "");
        const label = String(c.label || c.host || id);
        return `<option value="${esc(id)}">${esc(label)} · ${esc(String(c.host || ""))}</option>`;
      })
      .join("");
  };

  const fillDeviceSelect = () => {
    if (!deviceSel) return;
    if (!devices.length) {
      deviceSel.innerHTML = `<option value="">No owned boards</option>`;
      return;
    }
    deviceSel.innerHTML =
      `<option value="">Select board…</option>` +
      devices
        .map((d) => `<option value="${esc(d.id)}">${esc(d.label || d.id)}</option>`)
        .join("");
  };

  const renderCatalog = () => {
    if (!catalogEl) return;
    if (!cameras.length) {
      catalogEl.innerHTML = `<p class="muted" style="margin:0">No cameras yet — add one below, or discover on a board panel.</p>`;
      return;
    }
    catalogEl.innerHTML = `<table class="data-table"><thead><tr>
      <th>Label</th><th>Host</th><th>Protocol</th><th>Health</th><th>Credential</th><th></th>
    </tr></thead><tbody>${cameras
      .map((c) => {
        const id = String(c.camera_id || "");
        return `<tr>
          <td>${esc(String(c.label || "—"))}</td>
          <td class="mono">${esc(String(c.host || "—"))}</td>
          <td>${esc(String(c.protocol || "—"))}</td>
          <td>${esc(String(c.health_status || "—"))}</td>
          <td>${esc(String(c.credential_status || "—"))}</td>
          <td><button type="button" class="btn sm danger js-cam-fleet-del" data-cid="${esc(id)}">Delete</button></td>
        </tr>`;
      })
      .join("")}</tbody></table>`;
    catalogEl.querySelectorAll(".js-cam-fleet-del").forEach((btn) => {
      btn.addEventListener("click", async () => {
        const cid = btn.getAttribute("data-cid");
        if (!cid || !confirm("Delete this camera and cascade its bindings?")) return;
        try {
          await api(camPath(buildDeleteAdminCamerasPath(cid)), { method: "DELETE", timeoutMs });
          toast("Camera deleted", "ok");
          await loadCameras();
          await loadBindings();
        } catch (e) {
          toast(softApiError(e, "Delete failed"), "err");
        }
      });
    });
  };

  const loadCameras = async () => {
    setStatus("Loading…", "");
    try {
      const pack = await api(camPath(buildGetAdminCamerasPath()), { timeoutMs });
      if (!isRouteCurrent(routeSeq)) return;
      cameras = (pack && pack.items) || [];
      renderCatalog();
      fillCameraSelect();
      setStatus(`${cameras.length} camera${cameras.length === 1 ? "" : "s"}`, "ok");
    } catch (e) {
      if (!isRouteCurrent(routeSeq)) return;
      cameras = [];
      renderCatalog();
      fillCameraSelect();
      const soft = softOperatorNote(softApiError(e, "Could not load cameras"));
      setStatus(soft, "err");
      if (catalogEl) {
        catalogEl.innerHTML = `<p class="muted">${esc(soft)}</p>`;
      }
    }
  };

  const loadDevices = async () => {
    try {
      const pack = await api(buildGetDevicesPath(), { timeoutMs: DEVICES_CONSOLE_TIMEOUT_MS });
      if (!isRouteCurrent(routeSeq)) return;
      const rows = (pack && (pack.items || pack.devices)) || [];
      devices = (Array.isArray(rows) ? rows : [])
        .map((d) => {
          const id = String((d && (d.device_id || d.id)) || "").trim();
          if (!id) return null;
          const name = String((d && (d.name || d.label || d.nickname)) || "").trim();
          return { id, label: name ? `${name} (${id})` : id };
        })
        .filter(Boolean);
      fillDeviceSelect();
    } catch (_e) {
      if (!isRouteCurrent(routeSeq)) return;
      devices = [];
      fillDeviceSelect();
    }
  };

  const loadBindings = async () => {
    if (!bindingsEl || !deviceSel) return;
    const deviceId = String(deviceSel.value || "").trim();
    if (!deviceId) {
      bindingsEl.innerHTML = `<p class="muted" style="margin:0">Select a board to load bindings.</p>`;
      return;
    }
    bindingsEl.textContent = "Loading…";
    try {
      const pack = await api(camPath(buildGetAdminDevicesCameraBindingsPath(deviceId)), { timeoutMs });
      if (!isRouteCurrent(routeSeq)) return;
      const items = (pack && pack.items) || [];
      if (!items.length) {
        bindingsEl.innerHTML = `<p class="muted" style="margin:0">No camera bound to this board.
          <button type="button" class="btn sm ghost btn-tap" data-cc-go="devices" data-cc-id="${esc(deviceId)}">Open board</button> for network camera search.</p>`;
        return;
      }
      bindingsEl.innerHTML = `<table class="data-table"><thead><tr>
        <th>Camera</th><th>Cooldown</th><th>Enabled</th><th></th>
      </tr></thead><tbody>${items
        .map((b) => {
          const cam = cameras.find((c) => String(c.camera_id) === String(b.camera_id));
          const label = cam ? cam.label || cam.host : b.camera_id;
          const bid = String(b.binding_id || "");
          return `<tr>
            <td>${esc(String(label || ""))}</td>
            <td>${esc(String(b.cooldown_s ?? "—"))}s</td>
            <td>${esc(b.enabled === 0 || b.enabled === false ? "off" : "on")}</td>
            <td>
              <button type="button" class="btn sm secondary" data-cc-go="devices" data-cc-id="${esc(deviceId)}">Board</button>
              <button type="button" class="btn sm danger js-cam-fleet-unbind" data-bid="${esc(bid)}">Unbind</button>
            </td>
          </tr>`;
        })
        .join("")}</tbody></table>`;
      bindingsEl.querySelectorAll(".js-cam-fleet-unbind").forEach((btn) => {
        btn.addEventListener("click", async () => {
          const bid = btn.getAttribute("data-bid");
          if (!bid || !confirm("Remove binding?")) return;
          try {
            await api(camPath(buildDeleteAdminDevicesCameraBindingsPath(deviceId, bid)), {
              method: "DELETE",
              timeoutMs,
            });
            toast("Unbound", "ok");
            await loadBindings();
          } catch (e) {
            toast(softApiError(e, "Unbind failed"), "err");
          }
        });
      });
    } catch (e) {
      if (!isRouteCurrent(routeSeq)) return;
      bindingsEl.innerHTML = `<p class="muted">${esc((e && e.message) || "Could not load bindings.")}</p>`;
    }
  };

  if (deviceSel) {
    deviceSel.addEventListener("change", () => {
      void loadBindings();
    });
  }

  const addForm = view.querySelector("#camFleetAddForm");
  if (addForm) {
    addForm.addEventListener("submit", async (ev) => {
      ev.preventDefault();
      const host = String((view.querySelector("#camFleetHost") || {}).value || "").trim();
      if (!host) {
        toast("Host required", "err");
        return;
      }
      try {
        await api(camPath(buildPostAdminCamerasPath()), {
          method: "POST",
          timeoutMs,
          body: {
            label: String((view.querySelector("#camFleetLabel") || {}).value || "").trim(),
            host,
            protocol: String((view.querySelector("#camFleetProto") || {}).value || "onvif"),
            username: String((view.querySelector("#camFleetUser") || {}).value || ""),
            password: String((view.querySelector("#camFleetPass") || {}).value || ""),
          },
        });
        toast("Camera saved", "ok");
        const passEl = view.querySelector("#camFleetPass");
        if (passEl) passEl.value = "";
        await loadCameras();
      } catch (e) {
        toast(softApiError(e, "Save failed"), "err");
      }
    });
  }

  const probeBtn = view.querySelector("#camFleetProbe");
  if (probeBtn) {
    probeBtn.addEventListener("click", async () => {
      const host = String((view.querySelector("#camFleetHost") || {}).value || "").trim();
      if (!host) {
        toast("Host required", "err");
        return;
      }
      if (probeMsg) probeMsg.textContent = "Probing from API host…";
      try {
        const pack = await api(camPath(buildPostAdminCamerasProbeTestPath()), {
          method: "POST",
          timeoutMs,
          body: {
            host,
            protocol: String((view.querySelector("#camFleetProto") || {}).value || "onvif"),
            username: String((view.querySelector("#camFleetUser") || {}).value || ""),
            password: String((view.querySelector("#camFleetPass") || {}).value || ""),
          },
        });
        const ok = !!(pack && pack.ok);
        const hs = (pack && pack.health_status) || (ok ? "online" : "failed");
        if (probeMsg) {
          probeMsg.textContent = ok ? `Probe OK (${hs})` : `Probe failed (${hs})`;
          probeMsg.className = ok ? "badge online" : "badge offline";
        }
        toast(ok ? "Probe OK" : `Probe failed (${hs})`, ok ? "ok" : "err");
      } catch (e) {
        if (probeMsg) {
          probeMsg.textContent = (e && e.message) || "Probe failed";
          probeMsg.className = "badge offline";
        }
        toast(softApiError(e, "Probe failed"), "err");
      }
    });
  }

  const bindBtn = view.querySelector("#camFleetBindBtn");
  if (bindBtn) {
    bindBtn.addEventListener("click", async () => {
      const deviceId = String((deviceSel && deviceSel.value) || "").trim();
      const cameraId = String((camSel && camSel.value) || "").trim();
      const cooldownRaw = Number((view.querySelector("#camFleetCooldown") || {}).value);
      const cooldown_s = Number.isFinite(cooldownRaw) ? Math.max(5, Math.min(600, cooldownRaw)) : 20;
      if (!deviceId) {
        toast("Select a board", "err");
        return;
      }
      if (!cameraId) {
        toast("Select a camera", "err");
        return;
      }
      try {
        await api(camPath(buildPostAdminDevicesCameraBindingsPath(deviceId)), {
          method: "POST",
          timeoutMs,
          body: { camera_id: cameraId, cooldown_s },
        });
        toast("Bound", "ok");
        await loadBindings();
      } catch (e) {
        toast(softApiError(e, "Bind failed"), "err");
      }
    });
  }

  void (async () => {
    await Promise.all([loadCameras(), loadDevices()]);
  })();
}
